import { Injectable } from '@angular/core';
import { SettingsService } from '../settings/settings.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable } from 'rxjs';
import { BackendService } from '../backend/backend.service';
import { HttpHeaders } from '@angular/common/http';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { RoutesTable } from '../../resources/routes-table.model';
import { TokenType } from '../../enum/token-type.enum';
import { TokenCategory } from '../../enum/token-category.enum';
import { Token } from '../../models/authentication/token.model';
import { StorageService } from '../storage.service';
import { URLSearchParams } from '@angular/http';

let settingsServiceInstance: SettingsService;

interface ISendForgetPassword {
    email: string;
    captchaToken: string;
    captchaSiteKey: string;
    token: string;
}

@Injectable()
export class UserManagerService {

    public userRememberTokenKeyPrefix: string = 'all.';
    public static userRememberTokenKey: string = 'hub$user$remember$token';
    public static userPayloadStorageKey: string = 'hub$user$payload';
    public static userRealtimeStorageKey: string = 'hub$realtime$payload';
    
    constructor(
        private backendService: BackendService,
        private settingsService: SettingsService,
    ) {

        settingsServiceInstance = this.settingsService;
    }

    /**
     * Realiza a autenticação de um usuário junto a api
     *
     * @param username CPF, CNPJ ou e-mail do usuário cadastrado
     * @param password senha
     * @param captcha token do recaptcha
     */
    public authenticate(
        username: string,
        password: string,
        captchaSiteKey: string,
        captchaToken: string,
        cblcAccountCode: string = '',
        token: Token = null,
    ): Observable<any> {
        // tslint:disable-next-line: prefer-const
        let params = new URLSearchParams();
        password = encodeURIComponent(password);
        username = encodeURIComponent(username);

        params.set('username', username);
        params.set('password', password);
        params.set('client_id', this.settingsService.getConfigurationKeyValue('ci'));
        params.set('grant_type', 'password');
        // params.set('Content-Type', 'application/x-www-form-urlencode');
        const url = this.settingsService.baseUrl(RoutesTable.authentication.routes.login.uri);
        // request headers
        const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencode' });

        if (token) {
            params.set('X-TOKEN', token.value);
            params.set('X-TOKEN_TYPE', token.type);
            params.set('X-TOKEN_CATEGORY', token.category);
        }

        if (captchaToken && captchaSiteKey) {
            params.set('X-CAPTCHA', captchaToken);
            params.set('X-CAPTCHA-SITEKEY', captchaSiteKey);
        }

        if (cblcAccountCode && cblcAccountCode.length) {
            params.set('X-CBLC', cblcAccountCode);
        }

        return this.backendService.post(RoutesTable.authentication.routes.login.uri, decodeURIComponent(params.toString()));
    }

    /**
     * Autenticar um usuário a partir de um token
     *
     * @param token token time recebido via url (veio do e-mail)
     * @param username nome de usuário
     */
    public authenticateUserUsingComplexToken(token: string, username: string = ''): Observable<any> {
        // tslint:disable-next-line: prefer-const
        let params = new URLSearchParams();
        params.set('X-TOKEN_TYPE', TokenType.Time);
        params.set('X-TOKEN_CATEGORY', TokenCategory.Complex);
        params.set('X-TOKEN', token);

        if (username) params.set('username', username);

        params.set('client_id', this.settingsService.getConfigurationKeyValue('ci'));
        params.set('grant_type', 'password');

        // request headers
        const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencode' });
        const url = this.settingsService.baseUrl(RoutesTable.authentication.routes.login.uri);

        return this.backendService.post(RoutesTable.authentication.routes.login.uri, params.toString());
    }

    /**
     * Autenticar um usuário a partir de um token complexo vindo do webapp
     *
     * @param token token time recebido via url
     */
    public authenticateUserUsingComplexTokenFromWebApp(token: string): Observable<any> {
        // tslint:disable-next-line: prefer-const
        let params = new URLSearchParams();
        params.set('X-TOKEN_TYPE', TokenType.Time);
        params.set('X-TOKEN_CATEGORY', TokenCategory.HubMobile);
        params.set('X-TOKEN', token);
        params.set('client_id', this.settingsService.getConfigurationKeyValue('ci'));
        params.set('grant_type', 'password');

        const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencode' });
        const url = this.settingsService.baseUrl(RoutesTable.authentication.routes.login.uri);

        return this.backendService.post(RoutesTable.authentication.routes.login.uri, params.toString());
    }

    /**
     * Autenticar um usuário a partir de um token complexo vindo do e-mail de cadastro
     * @param token token time recebido via url
     */
    public authenticateUserUsingComplexTokenFromRegister(token: string): Observable<any> {
        // tslint:disable-next-line: prefer-const
        let params = new URLSearchParams();
        params.set('authentication_api_type', TokenCategory.RegistrationConclusion);
        params.set('X-TOKEN', token);
        params.set('client_id', this.settingsService.getConfigurationKeyValue('clientId'));
        params.set('grant_type', 'password');

        const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencode' });
        const url = this.settingsService.baseUrl(RoutesTable.authentication.routes.login.uri);

        return this.backendService.post(RoutesTable.authentication.routes.login.uri, params.toString());
    }

    /**
     * Método pra mudança de senha
     *
     * @param currentPassword senha atual
     * @param newPassword nova senha
     * @param jwtToken token de authenticação
     * @param token token de segunda autenticação
     */
    public changePassword(
        currentPassword: string,
        newPassword: string,
        jwtToken: string,
        token: Token = null,
        captchaToken: string = null,
        captchaSiteKey: string = null,
    ): Observable<DefaultResponse> {

        // let headers = new HttpHeaders({
        //     'Authorization': `Bearer ${jwtToken}`,
        //     'Content-Type': 'application/json',
        // });

        // if (token) {
        //     headers = headers.set('X-TOKEN', token.value);
        //     headers = headers.set('X-TOKEN_TYPE', token.type);
        //     headers = headers.set('X-TOKEN_CATEGORY', token.category);
        // }

        return this.backendService.post(
            RoutesTable.authentication.routes.updatePassword.uri,
            {
                newPassword,
                currentPassword,
                captchaToken,
                captchaSiteKey,
            },
            token,
        );
    }

    /**
     * Método pra mudança celular
     *
     * @param jwtToken token de authenticação
     * @param token
     */
    public changeEmail(jwtToken: string, token: Token = null): Observable<DefaultResponse> {

        // let headers = new HttpHeaders({
        //     'Authorization': `Bearer ${jwtToken}`,
        //     'Content-Type': 'application/json',
        // });

        // if (token) {
        //     headers = headers.set('X-TOKEN', token.value);
        //     headers = headers.set('X-TOKEN_TYPE', token.type);
        //     headers = headers.set('X-TOKEN_CATEGORY', token.category);
        // }

        return this.backendService.post(RoutesTable.authentication.routes.changeTelephone.uri, jwtToken, token);
    }

    /**
     * Executa a recuperação de senha
     * @param newPassoword
     * @param token
     */
    public recoverPassword(
        newPassoword: string,
        tokenUser: string,
        tokenSecondFactor: Token = null,
    ): Observable<any> {

        return this.backendService.post(
            RoutesTable.authentication.routes.recoverPassword.uri,
            {
                client_ID: this.settingsService.getConfigurationKeyValue('ci'),
                newPassword: newPassoword,
                token: tokenUser,
                tokenSecondFactor: tokenSecondFactor ? {
                    secret: tokenSecondFactor.value,
                    authenticationType: tokenSecondFactor.type,
                    category: tokenSecondFactor.category,
                    captcha: null,
                } : null,
            },
            tokenSecondFactor,
        );
    }

    /**
     * Envia um e-mail para recuperação de conta (desbloqueio)
     * @param username nome de usuário
     */
    public requestUnlockAccountEmail(username: string): Observable<any> {
        const url = RoutesTable.composeUri(RoutesTable.authentication.routes.requestUnlockAccountEmail, username);
        return this.backendService.get(url);
    }

    /**
     * Desbloquear conta
     *
     * @param firstFactor Token de primeiro fator
     * @param secondFactor Token de segundo fator
     */
    public unlockAccount(firstFactor: string, secondFactor: string = null): Observable<DefaultResponse> {
        const payload = {
            client_ID: this.settingsService.getConfigurationKeyValue('ci'),
            tokenFirstFactor: {
                secret: firstFactor,
                authenticationType: TokenType.Time,
                category: TokenCategory.Complex,
            },
            tokenSecondFactor: null,
        };

        if (secondFactor) {
            payload.tokenSecondFactor = {
                secret: secondFactor,
                authenticationType: TokenType.Time,
                category: TokenCategory.Complex,
            };
        }

        return this.backendService.post<DefaultResponse>(RoutesTable.authentication.routes.unlockAccount.uri, payload);

    }

    /**
     * Salvar o payload do usuário.
     *  @param payload payload da autenticação
     */
    public saveUserPayload(payload: string) {
        StorageService.setCookie(UserManagerService.userPayloadStorageKey, payload, null, null);
    }

    /**
     * Salvar o payload do realtime token.
     *  @param payload payload da autenticação recebendo o realtime token
     */
    public saveRealtimePayload(payload: string) {
        StorageService.setCookie(UserManagerService.userRealtimeStorageKey, payload, null, null);
    }

    /**
     * Realiza logout do usuário
     */
    public logout(): Observable<DefaultResponse> {
        return this.backendService.post<DefaultResponse>(RoutesTable.authentication.routes.logout.uri, null);
    }

    /** Limpra os dados do usuário */
    public clearUserData() {
        StorageService.deleteCookie(UserManagerService.userPayloadStorageKey);
        StorageService.deleteCookie(UserManagerService.userRealtimeStorageKey);
        sessionStorage.removeItem('userProfileData');
    }

    /**
     * Envia um e-mail pra recuperação de senha do usuário
     * @param email username do usuário
     */
    public sendForgotEmailPassword(data: ISendForgetPassword) {
        const payload = {
            username: data.email,
            clientID: this.settingsService.getConfigurationKeyValue('ci'),
            captcha: data.captchaToken,
            captchaSiteKey: data.captchaSiteKey
        };
        return this.backendService.post<DefaultResponse>(RoutesTable.authentication.routes.requestForgotPasswordEmail.uri, payload);
    }


    /**
     * Valida token enviado por e-mail pra recuperação de senha do usuário
     * @param email username do usuário
     */
    public validTokenForgotEmailPassword(data: ISendForgetPassword) {
        const payload = {
            email: data.email,
            client_ID: this.settingsService.getConfigurationKeyValue('ci'),
            token: data.token
        };
        return this.backendService.post<DefaultResponse>(RoutesTable.authentication.routes.validateTokenForgotPasswordEmail.uri, payload);
    }

    /**
     * Cria cookie que contém um token válido para lembrar do dispositivo
     * durante o segundo fator de altenticação.
     *
     * @param token
     */
    public saveUserRememberToken(
        token: string,
        keyPrefix: string = this.userRememberTokenKeyPrefix,
    ) {
        // Lembrar deste token por até 12 horas 
        const expireDate = new Date(Date.now() + (60 *1000) * (60 * 12))

        // Salva cookie no browser
        StorageService.setCookie(
            keyPrefix + UserManagerService.userRememberTokenKey,
            token,
            expireDate,
        );
    }

    /**
     * Retorna o token de 30 dias gerado para o dispositivo
     */
    public getUserRememberToken(keyPrefix: string = this.userRememberTokenKeyPrefix) {
        return StorageService.getCookie(keyPrefix + UserManagerService.userRememberTokenKey);
    }

    public getUserRealtimeToken() {
        return StorageService.getCookie(UserManagerService.userRealtimeStorageKey);
    }

    /**
     * Remove o token de 30 dias gerado para o dispositivo
     */
    public destroyRememberToken(keyPrefix: string = this.userRememberTokenKeyPrefix) {
        StorageService.deleteCookie(keyPrefix + UserManagerService.userRememberTokenKey);
    }

    /**
     * Remove o token JWT dos cookies
     */
    public destroyUserPayload() {
        this.saveUserPayload('');
    }

    /**
     * Extrai do token jwt os dados do usuário
     *
     * @param payload payload retornado pela api após a autenticação
     */
    public static extractUserData(payload: string): object {

        const jwtHelper = new JwtHelperService();

        try {
            const data = jwtHelper.decodeToken(payload);
            return {
                name: data.nameid,
                role: data.role,
                email: data.email,
                identifier: data.actort,
                token: payload,
                userId: data.UserID,
                isFirstActivatedLogin: data.IsFirstActivatedLogin,
                cblc: data.CBLC,
            };

        } catch (error) {
            return null;
        }
    }

    /**
     * Recupera e retorna os dados de um usuário que estava logado na aplicação
     */
    public static getPreviousUserData(): { [key: string]: any } | null {

        const userPayload = StorageService.cookies.get(UserManagerService.userPayloadStorageKey);
        const jwtHelper = new JwtHelperService();

        try {

            if (!userPayload || jwtHelper.isTokenExpired(userPayload)) {
                return null;
            }

            return UserManagerService.extractUserData(userPayload);

        } catch (error) {
            return null;
        }
    }

}
